import { module } from "modujs";
import { StampedApi } from "../class/StampedApi";

const REVIEWS_PER_PAGE = 4
const PAGINATION_ITEMS_LENGTH = 5

export default class extends module {
    constructor(m) {
        super(m);
    }

    async init() {
        // Prepare data
        this.productId = this.getData('id');
        this.apikey = this.getData('api-key');
        this.storeUrl = this.getData('store-url');

        // Init vue
        this.initVue();
    }

    initVue() { 
        /////////////////////////
        // Save moduleInstance
        const moduleInstance = this;

        /////////////////////////
        // Settings
        Vue.config.devtools = true;

        /////////////////////////
        // Vue Instance
        this.app = new Vue({
            el: this.el,

            delimiters: ["${", "}"],

            data: {
                sortedBy: 'featured',
                sortOptions: [
                    {
                        value: 'featured',
                        label: 'Featured'
                    },
                    {
                        value: 'highest-rating',
                        label: 'Highest Rating'
                    },
                    {
                        value: 'lowest-rating',
                        label: 'Lowest Rating'
                    },
                    {
                        value: 'photos',
                        label: 'Photos'
                    },
                    {
                        value: 'most-votes',
                        label: 'Most Votes'
                    },
                ],

                reviews: [],
                reviewCurrentPageIndex: 0,
                reviewTotal: 0,

                isLoadingReviews: false,
                isLoadingSummary: false,
                
                metrics: {}
            },

            computed: {
                // Get pagination buttons
                reviewPagination() {
                    return this.buildPagination(this.reviewCurrentPageIndex, this.reviewTotal, this.reviewPerPage, PAGINATION_ITEMS_LENGTH)
                },
                reviewPaginationLength() {
                    return Math.ceil(this.reviewTotal / this.reviewPerPage)
                }
            },

            watch: {
                sortedBy: function (newSort, oldSort) {
                    if (newSort === oldSort) return 
                    // Fetch new product review
                    this.fetchProductReviews(1, true)
                },
            },

            async beforeCreate() {
                this.moduleInstance = moduleInstance

                // Set data 
                this.reviewPerPage = REVIEWS_PER_PAGE;
            },
            
            created() {
                // Stamped Instance
                this.stampedInstance = new StampedApi({
                    storeUrl: this.moduleInstance.storeUrl,
                    apikey: this.moduleInstance.apikey
                })

                // Metrics
                this.defaultMetrics = {
                    countReviews: 0,
                    rating: 0,
                    breakdown: ['0', '0', '0', '0', '0'],
                    breakdownPercents: [-100, -100, -100, -100, -100]
                }

                this.metrics = {...this.metrics, ...this.defaultMetrics }

                // Update metrics
                this.fetchProductMetrics()
            },


            methods: {
                // Requests
                async fetchProductReviews(currentPageIndex = 1, forceFetch = false) {
                    if (!forceFetch && currentPageIndex === this.reviewCurrentPageIndex) return

                    // Loading state
                    this.isLoadingReviews = true

                    // Request
                    const reviews = await this.stampedInstance.getProductReviews(this.moduleInstance.productId, {
                        page: currentPageIndex,
                        take: this.reviewPerPage,
                        sort: this.sortedBy
                    })
                    
                    // Error
                    if (reviews.isError) {
                        // Loading state
                        this.isLoadingReviews = false
                        return console.log('error', reviews.error)
                    }

                    // Set data
                    this.reviewCurrentPageIndex = currentPageIndex
                    this.reviews = []
                    this.formatReviewData(reviews.result)
                },

                async fetchProductMetrics() {
                    // Loading state
                    this.isLoadingSummary = true

                    // Request
                    const ratings = await this.stampedInstance.getProductRatings(this.moduleInstance.productId)
                    
                    // Error
                    if (ratings.isError) {
                        console.log('error', ratings.error)
                        return 
                    }

                    // Set data
                    this.formatMetricsData(ratings.result)
                },


                // Formatters
                formatReviewData(reviews) {
                    if (!reviews?.data?.length) {
                        // Loading state
                        this.isLoadingReviews = false
                        return
                    }

                    // Global informations
                    const { total, data, translations } = reviews
                    this.reviewTotal = total

                    // For each review
                    data.forEach((reviewObj) => {
                        const { author, avatar, reviewDate, reviewTitle, reviewMessage, reviewVerifiedType, reviewRating } = reviewObj
                        let status = ''

                        if (reviewVerifiedType === 2) {
                            status = translations.label_verified_buyer
                        }

                        this.reviews.push({ author, avatar, status, reviewDate, reviewTitle, reviewMessage, reviewRating })
                    })

                    // Loading state
                    this.$nextTick(() => {
                        this.isLoadingReviews = false
                    })
                },

                formatMetricsData(metrics) {
                    if (!metrics.length) {
                        // Loading state
                        this.isLoadingSummary = false
                        return
                    }
                    
                    const {count, rating, breakdown} = metrics[0]

                    // Global informations
                    this.reviewTotal = count

                    // Metrics
                    const metricsObj = {
                        countReviews: count,
                        rating: Math.round(rating * 10) / 10,
                        breakdown: breakdown ? Object.values(breakdown).reverse() : [],
                        breakdownPercents: []
                    }

                    if (metricsObj.breakdown.length) {
                        metricsObj.breakdown.forEach(breakdown => {
                            metricsObj.breakdownPercents.push( -100 +  breakdown / this.reviewTotal * 100)
                        })
                    }

                    this.metrics = {...this.metrics, ...metricsObj }

                    // Loading state
                    this.$nextTick(() => {
                        this.isLoadingSummary = false

                        // Share rating
                        this.moduleInstance?.setProductFormRating?.(this.metrics.rating)
                    })
                },


                // Pagination builder
                buildPagination(currentItemIndex, itemsTotal, itemsPerPage, navigationLength) {
                    const items = []
                    const lastPageIndex = Math.ceil(itemsTotal / itemsPerPage)

                    // Ex: 2 pages but expect 6 buttons
                    if( lastPageIndex <= navigationLength) {
                        for (let index = 1; index < lastPageIndex + 1; index++) {
                            items.push({
                                index,
                                label: index.toString()
                            })
                        }

                    // Ex: current index is one of the latest page
                    } else if (currentItemIndex > lastPageIndex - navigationLength + 1 )  {
                        for (let index = lastPageIndex - navigationLength + 1; index < lastPageIndex + 1; index++) {
                            items.push({
                                index,
                                label: index.toString()
                            })
                        }

                    // Ex: current index is the first page
                    } else if (currentItemIndex === 1) {
                        for (let index = currentItemIndex; index < currentItemIndex + navigationLength - 1; index++) {
                            items.push({
                                index,
                                label: index === currentItemIndex + navigationLength - 2 ? '...' : index.toString(),
                            })
                        }

                        items.push({
                            index: lastPageIndex,
                            label: lastPageIndex.toString(),
                        })
                    
                    // Ex: first page < current index < last page - buttons length
                    } else {
                        for (let index = currentItemIndex - 1; index < currentItemIndex + navigationLength - 2; index++) {
                            items.push({
                                index,
                                label: index === currentItemIndex + navigationLength - 3 ? '...' : index.toString(),
                            })
                        }

                        items.push({
                            index: lastPageIndex,
                            label: lastPageIndex.toString(),
                        })
                    }

                    return items
                }
            }
        });
    }

    fetchData() {
        //this?.app?.fetchProductMetrics?.()
        this?.app?.fetchProductReviews?.()
    }

    setProductFormRating(rating) {
        this.call('setProductRating', rating, 'ProductForm')
    }

    destroy() {
        super.destroy();
        
        if (this.app) {
            this.app.$destroy();
        }
    }
}
